import { useState, useEffect } from 'react';

import { usePOST } from 'hooks/useRequest';

import CEFMessage from '../cef_message/cef_message';
import Form from '../cef_form/form';
import Textarea from '../cef_form/textarea';
import StarRating from './cef_star_rating';

import warningIconRed from '../../../assets/images/shared/icons/warning-red.png';

const FeedbackForm = ({ setFormStage }) => {
  const [radioOptions, setRadioOptions] = useState({
    improvements: { options: '', comments: '' },
    orderIssues: { options: '', comments: '' },
  });

  const [starRating, setStarRating] = useState(0);
  const [formData, setFormData] = useState(undefined);
  const [canSubmit, setCanSubmit] = useState(false);

  const {
    data: userFeedbackResponse,
    loading,
    error,
  } = usePOST(canSubmit ? '/user-feedback' : null, formData);

  useEffect(() => {
    if (userFeedbackResponse) {
      setFormStage(2);
    }
  }, [userFeedbackResponse]);

  useEffect(() => {
    if (error) {
      setCanSubmit(false);
    }
  }, [error]);

  const handleSubmit = e => {
    e.preventDefault();
    setFormData({ starRating, answers: radioOptions });
    setCanSubmit(true);
  };

  const handleRadio = e => {
    const { name, value } = e.target;
    setRadioOptions({
      ...radioOptions,
      [name]: { options: value },
    });
  };

  const handleComments = e => {
    const { name, value } = e.target;
    setRadioOptions({
      ...radioOptions,
      [name]: { options: 'yes', comments: value },
    });
  };

  const isFormValid = () => {
    const { improvements, orderIssues } = radioOptions;
    return (
      improvements.options !== '' &&
      orderIssues.options !== '' &&
      starRating > 0
    );
  };

  /**
   * Renders a radio button fieldset with a conditional dropdown text field
   * @param {string} label - Legend text label
   * @param {string} name  - used for label name/id attribute
   */
  const renderFieldsetDropdown = (label, name) => (
    <fieldset className="cef-feedback-form__fieldset">
      <div className="cef-feedback-form__legend-wrapper">
        <legend className="cef-feedback-form__label cef-feedback-form__legend">
          {label}
        </legend>

        <div className="fl ai-ic">
          <div className="cef-radio">
            <input
              value="yes"
              type="radio"
              name={name}
              id={`${name}_yes`}
              checked={radioOptions[name].options === 'yes'}
              onChange={handleRadio}
            />
            <label className="cef-radio__label" htmlFor={`${name}_yes`}>
              Yes
            </label>
          </div>

          <div className="cef-radio">
            <input
              value="no"
              type="radio"
              name={name}
              id={`${name}_no`}
              checked={radioOptions[name].options === 'no'}
              onChange={handleRadio}
            />
            <label className="cef-radio__label" htmlFor={`${name}_no`}>
              No
            </label>
          </div>
        </div>
      </div>

      {radioOptions[name].options === 'yes' && (
        <Textarea
          id={`${name}_comments`}
          name={`${name}`}
          value={radioOptions[name].comments}
          label="Additional comments"
          labelClassName="hide-text" // label is hidden from user - for screen reader purposes
          inputClassName="cef-feedback-form__textarea"
          handleChange={handleComments}
        />
      )}
    </fieldset>
  );

  return (
    <Form
      onSubmit={handleSubmit}
      className="cef-feedback-form"
      data-testid="feedback-form">
      <div className="cef-feedback-form__header">
        <p className="cef-feedback-form__heading">We value your feedback</p>
        <p className="cef-feedback-form__text">
          Help us improve our website experience by answering a few quick
          questions.
        </p>
      </div>

      <div className="cef-feedback-form__wrapper">
        <div className="cef-feedback-form__fieldset cef-feedback-form__fieldset--border-top">
          <fieldset className="cef-star-rating-wrapper">
            <div className="cef-feedback-form__legend-wrapper">
              <legend className="cef-feedback-form__label cef-feedback-form__legend">
                How would you rate your overall experience?
              </legend>
              <StarRating
                starRating={starRating}
                setStarRating={setStarRating}
              />
            </div>
          </fieldset>
        </div>

        {renderFieldsetDropdown(
          'Did you have any issues placing your order?',
          'orderIssues',
        )}

        {renderFieldsetDropdown(
          'Is there anything we could do to improve?',
          'improvements',
        )}

        {error && (
          <CEFMessage
            icon={warningIconRed}
            classNames="cef-message--danger cef-message--bold bg-white"
            text="Sorry, your feedback was not sent. Please try again"
          />
        )}

        <button
          type="submit"
          disabled={!isFormValid() || loading}
          className="mt-20 cef-btn cef-btn--green cef-btn--med cef-btn--block b lf">
          {loading ? <span className="cef-btn__loader" /> : 'Send Feedback'}
        </button>
      </div>
    </Form>
  );
};

export default FeedbackForm;
